import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Search, Edit2, Trash2, ExternalLink, Circle, CheckCircle2 } from "lucide-react";
import api, { formatApiError, resolveMediaUrl } from "@/lib/api";
import { toast } from "sonner";

const filters = [
  { key: "all", label: "All" },
  { key: "published", label: "Published" },
  { key: "draft", label: "Drafts" },
];

export default function BlogList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (q) params.q = q;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/admin/blogs", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [status]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blogs/${id}`);
      toast.success("Blog deleted");
      setItems((prev) => prev.filter((b) => b.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-7xl" data-testid="admin-blog-list">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="overline mb-3">Content</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Blogs</h1>
          <p className="mt-2 text-sm text-white/55">{total} total · {items.filter((i) => i.status === "published").length} published</p>
        </div>
        <button
          onClick={() => navigate("/admin/blogs/new")}
          data-testid="new-blog-btn"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold text-sm bg-white text-bg-0 hover:shadow-[0_0_35px_rgba(0,240,255,0.35)] transition"
        >
          <Plus size={15} /> New Blog
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="blog-search"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search blogs by title or slug…"
            className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 focus:outline-none focus:border-neon-cyan/50 transition text-sm"
          />
        </div>
        <div className="flex items-center gap-1 rounded-xl bg-white/[0.04] border border-white/10 p-1">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatus(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                status === f.key ? "bg-white text-bg-0" : "text-white/60 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 py-16 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-3xl p-14 text-center"
        >
          <p className="text-white/60">No blogs yet.</p>
          <button
            onClick={() => navigate("/admin/blogs/new")}
            className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold text-sm bg-white text-bg-0"
          >
            <Plus size={15} /> Write your first post
          </button>
        </motion.div>
      ) : (
        <div className="grid gap-3">
          {items.map((b, i) => (
            <motion.div
              key={b.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.4) }}
              className="group glass rounded-2xl p-4 sm:p-5 grid grid-cols-[64px_1fr_auto] gap-4 items-center hover:border-white/20 transition-colors"
              data-testid={`blog-row-${b.id}`}
            >
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-bg-2 flex items-center justify-center relative">
                {b.featured_image ? (
                  <img src={resolveMediaUrl(b.featured_image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-mono text-[11px] text-white/30 tracking-[0.16em]">NO IMG</span>
                )}
              </div>

              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <StatusPill status={b.status} />
                  <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-white/40">
                    {new Date(b.updated_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-white/40">
                    · {b.reading_time} min read
                  </span>
                </div>
                <div className="font-semibold text-white truncate">{b.title}</div>
                <div className="text-xs text-white/45 truncate">/blog/{b.slug}</div>
              </div>

              <div className="flex items-center gap-1">
                {b.status === "published" && (
                  <a
                    href={`/blog/${b.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-lg text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center"
                    aria-label="View live"
                    title="View live"
                  >
                    <ExternalLink size={15} />
                  </a>
                )}
                <Link
                  to={`/admin/blogs/${b.id}/edit`}
                  data-testid={`edit-${b.id}`}
                  className="w-9 h-9 rounded-lg text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center"
                  aria-label="Edit"
                  title="Edit"
                >
                  <Edit2 size={15} />
                </Link>
                <button
                  onClick={() => del(b.id, b.title)}
                  data-testid={`del-${b.id}`}
                  className="w-9 h-9 rounded-lg text-white/60 hover:text-rose-300 hover:bg-rose-500/10 flex items-center justify-center"
                  aria-label="Delete"
                  title="Delete"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const isPub = status === "published";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-[0.16em] border ${
        isPub
          ? "border-emerald-400/30 text-emerald-300 bg-emerald-400/5"
          : "border-white/15 text-white/60 bg-white/5"
      }`}
    >
      {isPub ? <CheckCircle2 size={10} /> : <Circle size={10} />}
      {isPub ? "Published" : "Draft"}
    </span>
  );
}
